import React, { useState, useEffect } from 'react';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import { useAppSelector } from "../app/hooks";
import { fetchSuperAdminDashboard, SuperAdminDashboardSeries } from "../features/dashboardAPI";

type Timeframe = 'weekly' | 'monthly';

interface CustomTooltipPayloadItem {
    name?: string;
    value?: number | string;
    color?: string;
}

interface CustomTooltipProps {
    active?: boolean;
    payload?: CustomTooltipPayloadItem[];
    label?: string | number;
}

const SuperAdminDash: React.FC = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [timeframe, setTimeframe] = useState<Timeframe>('weekly'); // 'weekly' or 'monthly'
    const [apiData, setApiData] = useState<SuperAdminDashboardSeries | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const accessToken = useAppSelector((state) => state.auth.accessToken);

    useEffect(() => {
        setIsVisible(true);
    }, []);

    useEffect(() => {
        const loadDashboard = async () => {
            if (!accessToken) return;
            setLoading(true);
            setError(null);

            const { data, error } = await fetchSuperAdminDashboard(accessToken);
            if (error) {
                setError(error);
                setApiData(null);
            } else if (data) {
                setApiData(data);
            }
            setLoading(false);
        };

        loadDashboard();
    }, [accessToken]);

    // Transform data for charts
    const getCompaniesJobsData = () => {
        if (!apiData) return [];

        const today = new Date();
        const currentYear = today.getFullYear();
        const yearStart = new Date(currentYear, 0, 1);
        const diffDays = Math.floor((today.getTime() - yearStart.getTime()) / (1000 * 60 * 60 * 24));
        const currentWeekIndex = Math.floor(diffDays / 7);
        const weekLimit = Math.min(currentWeekIndex + 1, apiData.week_labels.length);
        const monthLimit = Math.min(today.getMonth() + 1, apiData.month_labels.length); // months are 0-based

        if (timeframe === 'weekly') {
            return apiData.week_labels.slice(0, weekLimit).map((label, index) => ({
                name: label,
                companies: apiData.companies.companies_weekly[index],
                jobs: apiData.jobs.jobs_weekly[index]
            }));
        } else {
            return apiData.month_labels.slice(0, monthLimit).map((label, index) => ({
                name: label,
                companies: apiData.companies.companies_monthly[index],
                jobs: apiData.jobs.jobs_monthly[index]
            }));
        }
    };

    const getCandidatesData = () => {
        if (!apiData) return [];

        const today = new Date();
        const currentYear = today.getFullYear();
        const yearStart = new Date(currentYear, 0, 1);
        const diffDays = Math.floor((today.getTime() - yearStart.getTime()) / (1000 * 60 * 60 * 24));
        const currentWeekIndex = Math.floor(diffDays / 7);
        const weekLimit = Math.min(currentWeekIndex + 1, apiData.week_labels.length);
        const monthLimit = Math.min(today.getMonth() + 1, apiData.month_labels.length);

        if (timeframe === 'weekly') {
            return apiData.week_labels.slice(0, weekLimit).map((label, index) => ({
                name: label,
                candidates: apiData.candidates.candidates_weekly[index]
            }));
        } else {
            return apiData.month_labels.slice(0, monthLimit).map((label, index) => ({
                name: label,
                candidates: apiData.candidates.candidates_monthly[index]
            }));
        }
    };

    const getMatchesData = () => {
        if (!apiData) return [];

        const today = new Date();
        const currentYear = today.getFullYear();
        const yearStart = new Date(currentYear, 0, 1);
        const diffDays = Math.floor((today.getTime() - yearStart.getTime()) / (1000 * 60 * 60 * 24));
        const currentWeekIndex = Math.floor(diffDays / 7);
        const weekLimit = Math.min(currentWeekIndex + 1, apiData.week_labels.length);
        const monthLimit = Math.min(today.getMonth() + 1, apiData.month_labels.length);

        if (timeframe === 'weekly') {
            return apiData.week_labels.slice(0, weekLimit).map((label, index) => ({
                name: label,
                matches: apiData.matches.matches_weekly_avg[index]
            }));
        } else {
            return apiData.month_labels.slice(0, monthLimit).map((label, index) => ({
                name: label,
                matches: apiData.matches.matches_monthly_avg[index]
            }));
        }
    };

    // Card stats from API data
    const totalJobs = apiData
        ? apiData.jobs.jobs_monthly.reduce((sum, n) => sum + n, 0)
        : 0;
    const totalCandidates = apiData && apiData.candidates.candidates_monthly.length > 0
        ? apiData.candidates.candidates_monthly[apiData.candidates.candidates_monthly.length - 1]
        : 0;
    const avgMatchesRaw = apiData
        ? apiData.matches.matches_monthly_avg.filter((n) => n > 0)
        : [];
    const avgMatches = avgMatchesRaw.length > 0
        ? Number((avgMatchesRaw.reduce((a, b) => a + b, 0) / avgMatchesRaw.length).toFixed(1))
        : 0;
    const totalCompanies = apiData
        ? apiData.companies.companies_monthly.reduce((sum, n) => sum + n, 0)
        : 0;

    const getVisitPerCandidateData = () => {
        // Calculate average visits per candidate
        if (!apiData) return [];

        const today = new Date();
        const currentYear = today.getFullYear();
        const yearStart = new Date(currentYear, 0, 1);
        const diffDays = Math.floor((today.getTime() - yearStart.getTime()) / (1000 * 60 * 60 * 24));
        const currentWeekIndex = Math.floor(diffDays / 7);
        const weekLimit = Math.min(currentWeekIndex + 1, apiData.week_labels.length);
        const monthLimit = Math.min(today.getMonth() + 1, apiData.month_labels.length);

        if (timeframe === 'weekly') {
            return apiData.week_labels.slice(0, weekLimit).map((label, index) => {
                const candidates = apiData.candidates.candidates_weekly[index];
                const jobs = apiData.jobs.jobs_weekly[index];
                const visitPerCandidate =
                    candidates > 0 ? Number((jobs / candidates).toFixed(1)) : 0;
                return {
                    name: label,
                    visits: visitPerCandidate,
                };
            });
        } else {
            return apiData.month_labels.slice(0, monthLimit).map((label, index) => {
                const candidates = apiData.candidates.candidates_monthly[index];
                const jobs = apiData.jobs.jobs_monthly[index];
                const visitPerCandidate =
                    candidates > 0 ? Number((jobs / candidates).toFixed(1)) : 0;
                return {
                    name: label,
                    visits: visitPerCandidate,
                };
            });
        }
    };

    const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white border border-gray-300 p-3 rounded-md shadow-xl">
                    <p className="text-gray-800 font-semibold mb-1">{label}</p>
                    {payload.map((entry: CustomTooltipPayloadItem, index: number) => (
                        <p key={index} className="text-sm" style={{ color: entry.color }}>
                            {entry.name}: {entry.value}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    if (!accessToken) {
        return (
            <div className='bg-white min-h-screen p-8 flex items-center justify-center'>
                <p className='text-gray-700 text-lg'>Authentication required to view the Super Admin Dashboard.</p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className='bg-white min-h-screen p-8 flex items-center justify-center'>
                <p className='text-gray-700 text-lg'>Loading Super Admin Dashboard...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className='bg-white min-h-screen p-8 flex items-center justify-center'>
                <p className='text-red-600 text-lg'>Failed to load dashboard: {error}</p>
            </div>
        );
    }

    return (
        <div className='bg-white min-h-screen p-8'>
            {/* Header */}
            {/* <div className={`mb-8 transition-all duration-1000 transform ${isVisible ? 'translate-y-0 opacity-100' : '-translate-y-10 opacity-0'}`}>
                <h1 className='text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2'>
                    Super Admin Dashboard
                </h1>
                <p className='text-gray-600'>Welcome to the Super Admin Dashboard</p>
            </div> */}

            {/* Stats Cards */}
            <div className={`grid grid-cols-1 md:grid-cols-4 gap-6 mb-8 transition-all duration-1000 delay-200 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
                <div className="bg-[#CDD2DC] rounded-md p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 text-white">
                    <h3 className="text-sm font-semibold text-[#17243E] mb-2">Total Jobs</h3>
                    <p className="text-3xl font-bold text-[#17243E]">{totalJobs}</p>
                </div>
                <div className="bg-[#CDD2DC] rounded-md p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 text-white">
                    <h3 className="text-sm font-semibold text-[#17243E] mb-2">Total Candidates</h3>
                    <p className="text-3xl font-bold text-[#17243E]">{totalCandidates}</p>
                </div>
                <div className="bg-[#CDD2DC] rounded-md p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 text-white">
                    <h3 className="text-sm font-semibold text-[#17243E] mb-2">Avg Matches</h3>
                    <p className="text-3xl font-bold text-[#17243E]">{avgMatches}</p>
                </div>
                <div className="bg-[#CDD2DC] rounded-md p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 text-white">
                    <h3 className="text-sm font-semibold text-[#17243E] mb-2">Companies</h3>
                    <p className="text-3xl font-bold text-[#17243E]">{totalCompanies}</p>
                </div>
            </div>

            {/* Timeframe Toggle */}
            <div className={`mb-6 flex justify-end gap-2 transition-all duration-1000 delay-300 transform ${isVisible ? 'translate-y-0 opacity-100' : '-translate-y-10 opacity-0'}`}>
                <button
                    onClick={() => setTimeframe('weekly')}
                    className={`px-6 py-2 rounded-md font-semibold transition-all duration-300 ${timeframe === 'weekly'
                        ? 'bg-black text-white hover:bg-gray-300'
                        : 'bg-gray-200 text-white shadow-lg shadow-bg-gray-200/50'
                        }`}
                >
                    Weekly
                </button>
                <button
                    onClick={() => setTimeframe('monthly')}
                    className={`px-6 py-2 rounded-md font-semibold transition-all duration-300 ${timeframe === 'monthly'
                        ? 'bg-black text-white hover:bg-gray-300'
                        : 'bg-gray-200 text-white shadow-lg shadow-bg-gray-200/50'
                        }`}
                >
                    Monthly
                </button>
            </div>

            {/* Graphs Grid */}
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                {/* Companies and Jobs Graph */}
                <div className={`bg-white rounded-md p-6 shadow-lg border border-gray-200 transition-all duration-1000 delay-400 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} hover:shadow-xl hover:scale-[1.02]`}>
                    <h2 className='text-xl font-bold mb-4 text-gray-800'>Companies and Jobs</h2>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={getCompaniesJobsData()}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                            <XAxis dataKey="name" stroke="#4B5563" tick={{ fontSize: 12 }} />
                            <YAxis stroke="#4B5563" tick={{ fontSize: 12 }} domain={['dataMin', 'dataMax']} />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend wrapperStyle={{ color: '#374151' }} />
                            <Bar dataKey="companies" fill="#10B981" animationDuration={1500} radius={[8, 8, 0, 0]} />
                            <Bar dataKey="jobs" fill="#F59E0B" animationDuration={1500} radius={[8, 8, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Candidates Graph */}
                <div className={`bg-white rounded-md p-6 shadow-lg border border-gray-200 transition-all duration-1000 delay-500 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} hover:shadow-xl hover:scale-[1.02]`}>
                    <h2 className='text-xl font-bold mb-4 text-gray-800'>Candidates</h2>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={getCandidatesData()}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                            <XAxis dataKey="name" stroke="#4B5563" tick={{ fontSize: 12 }} />
                            <YAxis stroke="#4B5563" tick={{ fontSize: 12 }} domain={['dataMin', 'dataMax']} />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend wrapperStyle={{ color: '#374151' }} />
                            <Line
                                type="monotone"
                                dataKey="candidates"
                                stroke="#A855F7"
                                strokeWidth={3}
                                dot={{ fill: '#A855F7', r: 5 }}
                                activeDot={{ r: 8 }}
                                animationDuration={1500}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                {/* Matches Graph */}
                <div className={`bg-white rounded-md p-6 shadow-lg border border-gray-200 transition-all duration-1000 delay-600 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} hover:shadow-xl hover:scale-[1.02]`}>
                    <h2 className='text-xl font-bold mb-4 text-gray-800'>Average Matches</h2>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={getMatchesData()}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                            <XAxis dataKey="name" stroke="#4B5563" tick={{ fontSize: 12 }} />
                            <YAxis stroke="#4B5563" tick={{ fontSize: 12 }} domain={['dataMin', 'dataMax']} />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend wrapperStyle={{ color: '#374151' }} />
                            <Bar dataKey="matches" fill="#10B981" animationDuration={1500} radius={[8, 8, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Visit Per Candidate Graph */}
                <div className={`bg-white rounded-md p-6 shadow-lg border border-gray-200 transition-all duration-1000 delay-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} hover:shadow-xl hover:scale-[1.02]`}>
                    <h2 className='text-xl font-bold mb-4 text-gray-800'>Visit Per Candidate</h2>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={getVisitPerCandidateData()}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                            <XAxis dataKey="name" stroke="#4B5563" tick={{ fontSize: 12 }} />
                            <YAxis stroke="#4B5563" tick={{ fontSize: 12 }} domain={['dataMin', 'dataMax']} />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend wrapperStyle={{ color: '#374151' }} />
                            <Line
                                type="monotone"
                                dataKey="visits"
                                stroke="#EC4899"
                                strokeWidth={3}
                                dot={{ fill: '#EC4899', r: 5 }}
                                activeDot={{ r: 8 }}
                                animationDuration={1500}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};

export default SuperAdminDash;